import {Injectable} from '@angular/core';
import {AccountService} from "../api/account.service";
import {ApiHelperService} from "./api-helper.service";
import {MixpanelService} from "../../services/mixpanel.service";
import {SessionToken} from "../model/SessionToken";
import {BehaviorSubject, from, Observable, Subject} from "rxjs";
import {filter, skip, tap} from 'rxjs/operators';
import {OAuthErrorEvent, OAuthEvent, OAuthResourceServerErrorHandler, OAuthService, TokenResponse} from 'angular-oauth2-oidc';
import * as Sentry from '@sentry/angular';
import {authPasswordFlowConfig} from './auth-password-flow.config';
import {_throw} from 'rxjs-compat/observable/throw';
import {HttpResponse} from '@angular/common/http';
import {HomeRedirectService} from '../../services/home-redirect.service';
import {VisibilityService} from '../../services/visibility.service';

@Injectable()
export class LatteOAuthResourceServerErrorHandler implements OAuthResourceServerErrorHandler {
  handleError(err: HttpResponse<any>): Observable<any> {
    // console.log('Error! ' + JSON.stringify(err));
    return _throw(err);
  }
}

@Injectable()
export class LoginHelperService {

  // store the URL so we can redirect after logging in
  redirectUrl: string = null;

  private _isLoggedInObs: BehaviorSubject<boolean>;
  private _tokenRefreshedObs: Subject<string>;

  public loggedIn = new Subject<string>();
  public loggedOut = new Subject();

  constructor(private apiHelperService: ApiHelperService,
              private oauthService: OAuthService,
              private visibilityService: VisibilityService/*,
              private accountService: AccountService*/) {
    // Tweak config for password flow
    // This is just needed b/c this demo uses both,
    // implicit flow as well as password flow

    this.oauthService.configure(authPasswordFlowConfig);
    // this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.events.subscribe((e: OAuthEvent) => {
      // console.log('oauth/oidc event', e);
      if (e instanceof OAuthErrorEvent && e.type === 'token_refresh_error') {
        // invalid refresh token, Sign Out
        Sentry.captureMessage('OAuth token_refresh_error — forcing logout', 'warning');
        this.doLogout();
      }
    });
    // this.oauthService.loadDiscoveryDocument();

    const hasToken = !!this.oauthService.getAccessToken();
    const isValid = this.oauthService.hasValidAccessToken();
    const hasRefreshToken = !!this.oauthService.getRefreshToken();

    this._isLoggedInObs = new BehaviorSubject(this.isAuthTokenAvailable());
    this._tokenRefreshedObs = new Subject();

    // Restore Sentry user context from the stored token on startup so that
    // returning users are identified in Sentry without needing to log in again.
    if (hasToken) {
      const storedUsername = localStorage.getItem('hos_username');
      if (storedUsername) {
        Sentry.setUser({username: storedUsername});
      }
    }

    // When the tab becomes visible again (e.g. after being left open overnight),
    // check if the token has expired and refresh silently if possible.
    this.visibilityService.pageVisibility$.pipe(
      skip(1),                        // ignore the initial emission on startup
      filter(isVisible => isVisible), // only act when tab becomes visible
    ).subscribe(() => {
      if (this._isLoggedInObs.value && !this.oauthService.hasValidAccessToken() && !!this.oauthService.getRefreshToken()) {
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Tab became visible with expired token — attempting silent refresh',
          level: 'warning',
        });
        this.oauthService.refreshToken()
          .then(() => {
            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Visibility refresh succeeded',
              level: 'info',
              data: {
                newExpiry: this.oauthService.getAccessTokenExpiration()
                  ? new Date(this.oauthService.getAccessTokenExpiration()).toISOString()
                  : null,
              },
            });
          })
          .catch(() => {
            Sentry.captureMessage('Visibility refresh failed — logging out', 'warning');
            this.doLogout();
          });
      }
    });

    if (hasToken && !isValid) {
      const expiration = this.oauthService.getAccessTokenExpiration();
      Sentry.setContext('startup_token_state', {
        expiredAt: expiration ? new Date(expiration).toISOString() : null,
        expiredAgoMs: expiration ? Date.now() - expiration : null,
        hasRefreshToken,
      });

      if (hasRefreshToken) {
        // Access token expired but refresh token still exists — attempt a silent
        // refresh so the user stays logged in without seeing the login form.
        Sentry.captureMessage('Startup: access token expired — attempting silent refresh', 'info');
        this.oauthService.refreshToken()
          .then(() => {
            Sentry.captureMessage('Startup: silent refresh succeeded — user remains logged in', 'info');
            this._isLoggedInObs.next(true);
          })
          .catch(() => {
            // Refresh token also expired or invalid — force a clean logout.
            Sentry.captureMessage('Startup: silent refresh failed — logging out', 'warning');
            this.doLogout();
          });
      } else {
        // No refresh token at all — nothing to do, login form will appear.
        Sentry.captureMessage('Startup: access token expired and no refresh token', 'warning');
      }
    }
  }

  public doLogin(username: string, password: string): Observable<TokenResponse> {
    // return this.accountService.getAuthToken(environment.apiConfig.clientId, username, password)  // Get auth token
    //   .do(authTokenWrapper => this.apiHelperService.setAuthToken(authTokenWrapper.authToken))
    //   .mergeMap(authTokenWrapper => this.accountService.getSessionToken(authTokenWrapper.authToken)) // Get session token
    //   .do(sessionTokenWrapper => {
    //     this.apiHelperService.setSessionToken(sessionTokenWrapper);
    //     this.mixpanelService.identify(username);
    //     this.mixpanelService.track('login', {username: username});
    //     this._isLoggedInObs.next(true);
    //   });

    /*return this.accountService.getAuthToken(environment.apiConfig.clientId, username, password).pipe(
      tap(authTokenWrapper => this.apiHelperService.setAuthToken(authTokenWrapper.authToken)),
      mergeMap(authTokenWrapper => this.accountService.getSessionToken(authTokenWrapper.authToken)),
      tap(sessionTokenWrapper => {
        this.apiHelperService.setSessionToken(sessionTokenWrapper);
        this.mixpanelService.identify(username);
        this.mixpanelService.track('login', {username: username});
        this._isLoggedInObs.next(true);
      })
    );*/

    const oauthPromise = this.oauthService
    // .fetchTokenUsingPasswordFlowAndLoadUserProfile(
      .fetchTokenUsingPasswordFlow(username, password);

    return from(oauthPromise)
      .pipe(
        tap(value => {
          Sentry.setUser({username});
          localStorage.setItem('hos_username', username);
          this.loggedIn.next(username);
          this._isLoggedInObs.next(true);
        }));
  }

  public doLogout() {
    Sentry.setUser(null);
    localStorage.removeItem('hos_username');
    this.oauthService.logOut();
    this.loggedOut.next(true);
    this._isLoggedInObs.next(false);
  }

  get isLoggedIn$(): BehaviorSubject<boolean> {
    return this._isLoggedInObs;
  }

  refreshAccessToken(): Promise<Object> {
    return this.oauthService.refreshToken();
  }

  /*get tokenRefreshed(): Subject<string> {
    return this._tokenRefreshedObs;
  }*/

  public isAuthTokenAvailable() {
    return this.oauthService.hasValidAccessToken();
  }

  // Returns true if the access token is expired or will expire within the
  // given buffer (default 60 s). Used by the interceptor to refresh proactively
  // and avoid clock-skew 401s from the server.
  public isTokenExpiringSoon(bufferSeconds = 300): boolean {
    const expiration = this.oauthService.getAccessTokenExpiration();
    if (!expiration) return true;
    return Date.now() >= expiration - bufferSeconds * 1000;
  }

  /*public refreshSessionToken(): Observable<SessionToken> {
    return this.accountService.getSessionToken(this.apiHelperService.getAuthToken()).pipe(
      tap(sessionTokenWrapper => {
        this.apiHelperService.setSessionToken(sessionTokenWrapper); // set the new token
      })
    );
  }*/

  get access_token() {
    return this.oauthService.getAccessToken();
  }

  get access_token_expiration() {
    return this.oauthService.getAccessTokenExpiration();
  }

  get refresh_token() {
    return this.oauthService.getRefreshToken();
  }
}
